import { useQueryClient } from '@tanstack/react-query'
import { useItineraryContextOptional } from 'tripPlanner/assistant/contexts/ItineraryContext'
import type { TripItineraryData } from 'tripPlanner/api/types'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { useCallback, useContext, useState } from 'react'
import CurrencyContext from 'contexts/currencyContext'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { searchFlights } from 'api/flights'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { isLeFlightSavedItemData } from 'tripPlanner/api/types'
import type { ModalContextType } from 'contexts/ModalContext'
import { reportLog } from 'services/userMonitoring'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { parseSearchParams, isBookedFlightOption } from './flightShortlistUtils'
import { getCachedJourney } from './shortlistedJourneyCache'
import { prepareSelectedFlightCheckoutItem } from './prepareFlightCheckoutItem'
import { findShortlistedFlightJourneys } from './shortlistedFlightJourneyMatching'
import type { BookableFlightOption, BookFlightResult } from 'tripPlanner/components/Chat/Flights/flightRecommendationBookingTypes'

interface UseBookResult {
  bookFlight: (option: BookableFlightOption) => Promise<BookFlightResult>
  /** Books every leg of a multi-city itinerary into one checkout. */
  bookFlightGroup: (legs: Array<BookableFlightOption>) => Promise<BookFlightResult>
  bookingItemId: number | undefined
  searchingItemId: number | undefined
}

function isBookableCachedJourney(entry?: { journey: App.JourneyV2; searchId?: string }): boolean {
  if (!entry?.searchId) return false

  const firstFlight = entry.journey.flightGroup?.flights?.[0]
  return !!(
    entry.journey.price?.all?.totalFare != null
    && firstFlight?.marketingAirline?.code
  )
}

function useBookShortlistedFlight(showModal: ModalContextType): UseBookResult {
  const [bookingItemId, setBookingItemId] = useState<number | undefined>(undefined)
  const [searchingItemId, setSearchingItemId] = useState<number | undefined>(undefined)
  const intl = useIntl()
  const queryClient = useQueryClient()
  const itineraryContext = useItineraryContextOptional()
  const chatId = itineraryContext?.itineraryData?.tripChatId
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const email = useAppSelector(state => state.auth.account.email)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && region === 'AU'

  // Drawers can retain an older option while another surface changes its booking.
  const isCurrentlyBooked = useCallback((option: BookableFlightOption) => {
    if (!chatId) return false
    const itinerary = queryClient.getQueryData<TripItineraryData>(ChatKeys.itineraryDetail(chatId, region))
    return itinerary?.stops.some(stop => stop.transports?.some(connection => connection.options.some(current => (
      (current.itemId === option.itemId || current.returnLeg?.itemId === option.itemId) && isBookedFlightOption(current)
    )))) ?? false
  }, [chatId, queryClient, region])

  const bookFlight = useCallback(async(option: BookableFlightOption): Promise<BookFlightResult> => {
    if (!isLeFlightSavedItemData(option.savedItemData)) return 'error'

    if (isCurrentlyBooked(option)) return 'cancelled'
    const data = option.savedItemData
    const params = parseSearchParams(data.searchParams)
    if (!params) {
      reportLog('[TripChat] Flight recommendation booking missing search params', {
        itemId: option.itemId,
        hasJourneyKey: !!data.journeyKey,
        hasSearchId: !!data.searchId,
      }, 'warn')
      showSnackbar(intl.formatMessage({
        id: 'R6wd52-oZj65JhpjjL7G',
        defaultMessage: 'Unable to book this flight. Search parameters are missing.',
        description: 'Error snackbar shown when a shortlisted flight is missing search parameters needed for booking',
      }), 'critical')
      return 'missing_search_params'
    }

    setBookingItemId(option.itemId)

    try {
      const returnLeg = option.returnLeg
      const returnLegData = isLeFlightSavedItemData(returnLeg?.savedItemData) ? returnLeg.savedItemData : undefined
      const isReturn = !!returnLeg && !!returnLegData
      const fareType: 'return' | 'oneWay' = params.fareType === 'return' ? 'return' : 'oneWay'

      const flightUnavailableMessage = intl.formatMessage({
        id: 'hqp07ZRkaLAescD38LIo',
        defaultMessage: 'This flight is no longer available. Please search for new flights.',
        description: 'Warning snackbar shown when a shortlisted flight cannot be found in current search results',
      })

      let matchedDepartingJourney: App.JourneyV2 | undefined
      let matchedReturningJourney: App.JourneyV2 | undefined
      let searchId: string | undefined
      let sessionId: string | undefined
      let searchExpiry: number

      const departingCached = data.journeyKey ? getCachedJourney(data.journeyKey, canRequestLuxPlusFlightPricing) : undefined
      const returningCached = returnLegData?.journeyKey ? getCachedJourney(returnLegData.journeyKey, canRequestLuxPlusFlightPricing) : undefined

      const departingCacheValid = !!departingCached && isBookableCachedJourney(departingCached)
      const returningCacheValid = !isReturn || (!!returningCached && isBookableCachedJourney(returningCached))

      if (departingCached && departingCacheValid && returningCacheValid) {
        matchedDepartingJourney = departingCached.journey
        matchedReturningJourney = returningCached?.journey
        searchId = departingCached.searchId
        sessionId = departingCached.sessionId
        searchExpiry = departingCached.cachedAt + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000
      } else {
        setSearchingItemId(option.itemId)

        const searchResult = await searchFlights({
          itinerary: params.itinerary,
          currency,
          occupancies: params.occupancies,
          fareClass: params.fareClass,
          region,
          fareType: params.fareType,
          email,
        }, {
          enableSabreContent: true,
          ...(canRequestLuxPlusFlightPricing ? { requestLuxPlusPricing: true } : {}),
        })

        setSearchingItemId(undefined)
        searchId = searchResult.searchId
        sessionId = searchResult.sessionId
        searchExpiry = Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000

        if (isReturn) {
          const departingFares = searchResult.onewayFares[0] ?? []
          const returningFares = searchResult.onewayFares[1] ?? []

          const matchedJourneys = findShortlistedFlightJourneys({
            onewayFares: searchResult.onewayFares,
            departingJourneyKey: data.journeyKey,
            returningJourneyKey: returnLegData.journeyKey,
          })
          matchedDepartingJourney = matchedJourneys.departingJourney
          matchedReturningJourney = matchedJourneys.returningJourney

          if (!matchedDepartingJourney || !matchedReturningJourney) {
            reportLog('[TripChat] Return flight recommendation unavailable after refresh', {
              itemId: option.itemId,
              fareType,
              hasDepartingJourneyKey: !!data.journeyKey,
              hasReturningJourneyKey: !!returnLegData.journeyKey,
              matchedDeparting: !!matchedDepartingJourney,
              matchedReturning: !!matchedReturningJourney,
              departingFareCount: departingFares.length,
              returningFareCount: returningFares.length,
            }, 'warn')
            showSnackbar(flightUnavailableMessage, 'warning')
            return 'unavailable'
          }
        } else {
          const allFares = searchResult.onewayFares.flat()
          const matchedJourneys = findShortlistedFlightJourneys({
            onewayFares: searchResult.onewayFares,
            departingJourneyKey: data.journeyKey,
          })
          matchedDepartingJourney = matchedJourneys.departingJourney

          if (!matchedDepartingJourney) {
            reportLog('[TripChat] One-way flight recommendation unavailable after refresh', {
              itemId: option.itemId,
              fareType,
              hasJourneyKey: !!data.journeyKey,
              fareCount: allFares.length,
            }, 'warn')
            showSnackbar(flightUnavailableMessage, 'warning')
            return 'unavailable'
          }
        }
      }

      if (!searchId) {
        reportLog('[TripChat] Flight recommendation booking missing search id', {
          itemId: option.itemId,
          fareType,
          hasDepartingJourney: !!matchedDepartingJourney,
          hasReturningJourney: !!matchedReturningJourney,
        }, 'warn')
        showSnackbar(flightUnavailableMessage, 'warning')
        return 'unavailable'
      }

      const preparedResult = await prepareSelectedFlightCheckoutItem({
        departingJourney: matchedDepartingJourney,
        returningJourney: matchedReturningJourney,
        searchParams: params,
        searchId,
        sessionId,
        searchExpiry,
        showModal,
      })

      if (preparedResult.status === 'cancelled') return 'cancelled'
      if (preparedResult.status !== 'success') return 'error'

      if (isCurrentlyBooked(option)) return 'cancelled'
      dispatch(addToCheckoutCart([preparedResult.prepared.checkoutItem]))
      dispatch(navigateToCheckout())
      return 'success'
    } catch(error) {
      reportLog('[TripChat] Flight recommendation booking failed', {
        itemId: option.itemId,
        error: error instanceof Error ? error.message : 'Unknown error',
        hasJourneyKey: !!data.journeyKey,
        hasSearchId: !!data.searchId,
      }, 'error')
      showSnackbar(intl.formatMessage({
        id: 'Xi8uEjkdq0mQ8_7rn8QW',
        defaultMessage: 'Something went wrong while preparing your booking. Please try again.',
        description: 'Error snackbar shown when booking a shortlisted flight fails unexpectedly',
      }), 'critical')
      return 'error'
    } finally {
      setBookingItemId(undefined)
      setSearchingItemId(undefined)
    }
  }, [dispatch, currency, region, showModal, intl, email, canRequestLuxPlusFlightPricing, isCurrentlyBooked])

  /**
   * Multi-city legs are separate saved items, but they are one booking. Every
   * leg persists the same full itinerary, so a single search covers all of them:
   * match each leg's journey in the results, prepare a checkout item per leg,
   * then add them to the cart together and go to checkout once.
   */
  const bookFlightGroup = useCallback(async(legs: Array<BookableFlightOption>): Promise<BookFlightResult> => {
    if (legs.some(isCurrentlyBooked)) return 'cancelled'
    const firstLeg = legs[0]
    if (!firstLeg || !isLeFlightSavedItemData(firstLeg.savedItemData)) return 'error'

    const params = parseSearchParams(firstLeg.savedItemData.searchParams)
    if (!params) {
      reportLog('[TripChat] Multi-city flight booking missing search params', {
        itemId: firstLeg.itemId,
        legCount: legs.length,
      }, 'warn')
      showSnackbar(intl.formatMessage({
        id: 'R6wd52-oZj65JhpjjL7G',
        defaultMessage: 'Unable to book this flight. Search parameters are missing.',
        description: 'Error snackbar shown when a shortlisted flight is missing search parameters needed for booking',
      }), 'critical')
      return 'missing_search_params'
    }

    const flightUnavailableMessage = intl.formatMessage({
      id: 'hqp07ZRkaLAescD38LIo',
      defaultMessage: 'This flight is no longer available. Please search for new flights.',
      description: 'Warning snackbar shown when a shortlisted flight cannot be found in current search results',
    })

    setBookingItemId(firstLeg.itemId)

    try {
      setSearchingItemId(firstLeg.itemId)
      const searchResult = await searchFlights({
        itinerary: params.itinerary,
        currency,
        occupancies: params.occupancies,
        fareClass: params.fareClass,
        region,
        fareType: params.fareType,
        email,
      }, {
        enableSabreContent: true,
        ...(canRequestLuxPlusFlightPricing ? { requestLuxPlusPricing: true } : {}),
      })
      setSearchingItemId(undefined)

      const searchId = searchResult.searchId
      if (!searchId) {
        reportLog('[TripChat] Multi-city flight booking missing search id', {
          itemId: firstLeg.itemId,
          legCount: legs.length,
        }, 'warn')
        showSnackbar(flightUnavailableMessage, 'warning')
        return 'unavailable'
      }

      const searchExpiry = Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000
      const checkoutItems: Array<App.Checkout.FlightItem> = []

      for (const leg of legs) {
        const legData = isLeFlightSavedItemData(leg.savedItemData) ? leg.savedItemData : undefined
        const { departingJourney } = findShortlistedFlightJourneys({
          onewayFares: searchResult.onewayFares,
          departingJourneyKey: legData?.journeyKey,
        })

        if (!departingJourney) {
          reportLog('[TripChat] Multi-city flight leg unavailable after refresh', {
            itemId: leg.itemId,
            legCount: legs.length,
            hasJourneyKey: !!legData?.journeyKey,
          }, 'warn')
          showSnackbar(flightUnavailableMessage, 'warning')
          return 'unavailable'
        }

        const preparedResult = await prepareSelectedFlightCheckoutItem({
          departingJourney,
          searchParams: params,
          searchId,
          sessionId: searchResult.sessionId,
          searchExpiry,
          showModal,
        })

        if (preparedResult.status === 'cancelled') return 'cancelled'
        if (preparedResult.status !== 'success') return 'error'
        checkoutItems.push(preparedResult.prepared.checkoutItem)
      }

      if (legs.some(isCurrentlyBooked)) return 'cancelled'
      // One dispatch so checkout opens with the whole itinerary, never a partial one.
      dispatch(addToCheckoutCart(checkoutItems))
      dispatch(navigateToCheckout())
      return 'success'
    } catch(error) {
      reportLog('[TripChat] Multi-city flight booking failed', {
        itemId: firstLeg.itemId,
        legCount: legs.length,
        error: error instanceof Error ? error.message : 'Unknown error',
      }, 'error')
      showSnackbar(intl.formatMessage({
        id: 'Xi8uEjkdq0mQ8_7rn8QW',
        defaultMessage: 'Something went wrong while preparing your booking. Please try again.',
        description: 'Error snackbar shown when booking a shortlisted flight fails unexpectedly',
      }), 'critical')
      return 'error'
    } finally {
      setBookingItemId(undefined)
      setSearchingItemId(undefined)
    }
  }, [dispatch, currency, region, showModal, intl, email, canRequestLuxPlusFlightPricing, isCurrentlyBooked])

  return { bookFlight, bookFlightGroup, bookingItemId, searchingItemId }
}

export default useBookShortlistedFlight
